/**
 * Copyright 2023 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import cx from 'classnames';
import { forwardRef, useRef } from 'react';
import { MobileHeaderSignIn } from '../MobileHeaderSignIn/MobileHeaderSignIn';
import Hidden from '@/components/Hidden';
import { TopNavStore } from '@/features/topNav/components/Store';
import { Cart } from '@/features/topNav/components/Cart';
import { SmartLink as Link } from '@/utils/smartLink';
import { MenuNav } from '../MenuNav/MenuNav';
import AZLogo from '@/components/AZCustomComponent/AZLogo';
import { dataTestIds } from '../constants';
import styles from './fixedHeader.module.scss';
import dynamic from 'next/dynamic';
import { countryCodes } from '@/constants/locale';
import { useLocale } from '@/hooks/useLocale';

const LazyMyAccount = dynamic(() =>
  import('@/components/MyAccount/myAccount').then((mod) => mod.MyAccount)
);

export type FixedTopHeaderProps = {
  isSearchFocused: boolean;
  scrolled: boolean;
  programaticallyScrolled: boolean;
  headerRef: React.RefObject<HTMLDivElement>;
  vehicleComponent: JSX.Element | null;
  searchBoxComponent: JSX.Element | null;
  toastComponent: JSX.Element | null;
  updateShowToolTip: (isMenuOpen: boolean) => void;
};

const FixedTopHeader = forwardRef<HTMLDivElement | null, FixedTopHeaderProps>(
  function FixedTopHeader(
    {
      isSearchFocused,
      scrolled,
      programaticallyScrolled,
      updateShowToolTip,
      vehicleComponent,
      searchBoxComponent,
      toastComponent,
    },
    ref
  ) {
    const searchBarRef = useRef<HTMLDivElement | null>(null);
    const showTopNavStore = !(
      isSearchFocused && (searchBarRef.current?.offsetParent?.clientWidth || 1350) < 1350
    );
    const locale = useLocale();

    return (
      <div
        id="scrollFixedHeaderContainer"
        className={scrolled || programaticallyScrolled ? undefined : styles.fixedTopHeaderHidden}
        data-testid="fixedHeader"
      >
        <header
          id="fixedHeaderWrapper"
          className={cx(styles.headerNavBar, styles.scrollFixedHeader)}
          role="banner"
        >
          <div
            className={styles.headerWrapperTopNav}
            data-testid="mobile-top-nav-header"
            id="top-nav-fixed-header"
            ref={ref}
          >
            <div
              data-testid="menu-nav-fixed-top"
              className={cx(styles.part1, { [styles.hidden]: isSearchFocused })}
            >
              <MenuNav
                id="menuNavBtnFixHeader"
                onToggle={updateShowToolTip}
                dataTestIdBtn={dataTestIds.menuNavButtonFixedHeader}
                scrolled={scrolled || programaticallyScrolled}
                isFixedHeader
              />
              <div className={cx(styles.addInfoItems, styles.logoItem)}>
                <Link id="azLogoLinkFixed" to="/">
                  <AZLogo
                    variant="SmallSize"
                    customClassName={styles.autoZoneLogo}
                    containerClassName={styles.containerLogoSticky}
                  />
                </Link>
              </div>
              <Hidden smDown>
                <div
                  data-testid="vehicle-menu-nav-fixed-top"
                  className={styles.vehicleContainerFixed}
                >
                  {vehicleComponent}
                </div>
              </Hidden>
            </div>
            <div
              className={cx(styles.persistSearchDesktopField, {
                [styles.searchDesktopFocused]: isSearchFocused,
              })}
            >
              <div
                id="searchBoxCompContainer"
                className={styles.mobileSearchItem}
                ref={searchBarRef}
              >
                {searchBoxComponent}
              </div>
            </div>
            <div className={cx(styles.part3, { [styles.hidden]: isSearchFocused })}>
              <Hidden smDown>
                {showTopNavStore && (
                  <div className={styles.selectStore}>
                    <div className={styles.mobileAddInfoItems}>
                      <TopNavStore isFixedHeader dataTestIdStoreName={dataTestIds.storeNameText} />
                    </div>
                  </div>
                )}
              </Hidden>
              {locale !== countryCodes.ptBr && (
                <div className={styles.headerActionButtonsContainer}>
                  <Hidden implementation="js" mdDown>
                    <div className={cx(styles.desktopAccountWrapperSticky, styles.hoverInfoItems)}>
                      <LazyMyAccount isFixedHeader />
                    </div>
                  </Hidden>
                  <MobileHeaderSignIn isFixedHeader />
                  <div className={styles.fixedHeaderCartButton}>
                    <Cart dataTestIdCartLink={dataTestIds.cartLink} isFixedHeader />
                  </div>
                </div>
              )}
            </div>
          </div>
        </header>
        {toastComponent}
      </div>
    );
  }
);

export default FixedTopHeader;
